/**
 * ルートコンポーネント。公開マップ画面。
 * 地図（MapView）を全面表示し、公開中リスト（アコーディオン）から地図移動できる。
 *
 * ローディングオーバーレイは「地図の初期ロード完了」かつ「初回データ取得完了（または失敗）」
 * の両方が揃うまで表示する。これにより「地図は見えるがピンがまだ」の空白時間を隠し、
 * オーバーレイが消えた時点でピンが揃っている体感にする。
 */
import { useEffect, useRef, useState } from 'react';
import { MapView } from './map/MapView';
import { usePins } from './map/usePins';
import { usePosts } from './map/usePosts';
import { preloadAvatars } from './map/avatarPreload';
import { getMyProfile, deletePost } from './api/client';
import { loadTokens } from './auth/cognito';
import { ParticipantPanel } from './panel/ParticipantPanel';
import { ActivityFeed } from './feed/ActivityFeed';
import { useFeedEnabled } from './feed/feedSetting';
import { LoginControl } from './auth/LoginControl';
import { CheckinControl } from './auth/CheckinControl';
import { ProfileControl } from './auth/ProfileControl';
import { StatusControl } from './auth/StatusControl';
import { PostControl } from './auth/PostControl';
import { UserMenu } from './auth/UserMenu';
import { TrackingControl } from './auth/TrackingControl';
import { useTracking } from './auth/useTracking';
import { useAuth } from './auth/useAuth';
import { useLang } from './i18n/lang';
import { useMapStyle } from './map/mapStyle';
import { messages } from './i18n/messages';

export function App() {
  const { data, error, loading } = usePins();
  const { data: postsData, refresh: refreshPosts } = usePosts();
  // 認証は App で一度だけ実行し、各コントロールに渡す（コールバック重複実行を防ぐ）
  const auth = useAuth();
  // Web トラッキング（タスク48）。メニュー内のコントロールは閉じるとアンマウントされるので、状態は App で持つ。
  const tracking = useTracking(auth);
  // 言語状態（日本語/英語・localStorage 永続化）。地図と UI に配布する。
  const { lang, setLang } = useLang();
  // 背景スタイル（標準/モノクロ/衛星・localStorage 永続化）。地図と切替 UI に配布する。
  const { mapStyle, setMapStyle } = useMapStyle();
  // ライブアクティビティフィードの ON/OFF（既定 ON・localStorage 永続化・タスク54）。
  const { feedEnabled, toggleFeed } = useFeedEnabled();
  // 公開中パネルの開閉。開いている間はフィードを出さない（スマホ縦画面で下に溢れるため・案b）。
  const [panelOpen, setPanelOpen] = useState(false);

  // ログイン中ユーザーの user_id（g_<sub>）。自分のポスト削除ボタンの表示判定に使う。
  const currentUserId = auth.user ? `g_${auth.user.sub}` : null;

  const users = data?.users ?? [];
  const serverTime = data?.server_time ?? Math.floor(Date.now() / 1000);
  const posts = postsData?.posts ?? [];
  const postsServerTime = postsData?.server_time;

  // MapView の地図移動関数を受け取るハンドル
  const flyToRef = useRef<((lng: number, lat: number) => void) | null>(null);

  // 地図の初期ロード完了
  const [mapReady, setMapReady] = useState(false);

  // アコーディオンで選択中のユーザー（軌跡強調用）
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // プロフィール編集後の左下ログイン表示の上書き（案A・マップと表示名/アイコンを揃える）。
  // undefined のときは Google アカウントの値をそのまま表示する。
  const [profileOverride, setProfileOverride] = useState<
    { displayName?: string; avatarHidden?: boolean } | undefined
  >(undefined);

  // アバター画像を先読みしてピン描画時の顔画像遅延を軽減（B）
  useEffect(() => {
    if (data) {
      preloadAvatars(users);
    }
  }, [data, users]);

  // ログイン確定後、本人の保存済みプロフィールを取得して左下表示を揃える（リロード後も一貫）。
  // 保存済み display_name があれば常に上書きする（Google 名と同値かの比較はしない。
  // 比較で揃えようとすると表記ゆれ等で取りこぼし、リロード後に Google 名へ戻る不具合になる）。
  useEffect(() => {
    if (!auth.enabled || auth.loading || !auth.user) {
      setProfileOverride(undefined);
      return;
    }
    const token = loadTokens()?.id_token;
    if (!token) {
      return;
    }
    let disposed = false;
    (async () => {
      try {
        const profile = await getMyProfile(token);
        if (disposed) {
          return;
        }
        const override: { displayName?: string; avatarHidden?: boolean } = {};
        if (profile.display_name) {
          override.displayName = profile.display_name;
        }
        if (profile.avatar_hidden) {
          override.avatarHidden = true;
        }
        setProfileOverride(
          override.displayName !== undefined ||
            override.avatarHidden !== undefined
            ? override
            : undefined,
        );
      } catch {
        // 取得失敗時は Google 値のまま（左下表示は従来どおり）
      }
    })();
    return () => {
      disposed = true;
    };
  }, [auth.enabled, auth.loading, auth.user]);

  // 自分のポストを削除する（マップ吹き出しの削除ボタン）。成功後に即リフレッシュ。
  const handleDeletePost = (postId: string): void => {
    const token = loadTokens()?.id_token;
    if (!token) {
      return;
    }
    void (async () => {
      try {
        await deletePost(token, postId);
        refreshPosts();
      } catch {
        // 失敗時は次ポーリングで整合（必要なら将来トースト通知）
      }
    })();
  };

  // 初回データ取得が終わったか（成功 or 失敗）。loading=false になれば初回完了。
  const dataSettled = !loading;
  // オーバーレイは「地図ロード完了」かつ「初回データ確定」まで（A）
  const showOverlay = !mapReady || !dataSettled;
  const m = messages(lang);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <MapView
        users={users}
        serverTime={serverTime}
        lang={lang}
        mapStyle={mapStyle}
        posts={posts}
        postsServerTime={postsServerTime}
        selectedUserId={selectedUserId}
        flyToRef={flyToRef}
        onReady={() => setMapReady(true)}
        onDeselect={() => setSelectedUserId(null)}
        onSelectUser={(userId) => setSelectedUserId(userId)}
        currentUserId={currentUserId}
        onDeletePost={handleDeletePost}
        onLangChange={setLang}
        onMapStyleChange={setMapStyle}
        feedEnabled={feedEnabled}
        onFeedToggle={toggleFeed}
      />
      {/* 左上: 公開中パネルと、その真下のライブアクティビティフィード（タスク54）。 */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 1,
          width: 200,
        }}
      >
        <ParticipantPanel
          users={users}
          loading={loading}
          error={!!error}
          lang={lang}
          open={panelOpen}
          onOpenChange={setPanelOpen}
          onSelect={(lng, lat, userId) => {
            setSelectedUserId(userId);
            flyToRef.current?.(lng, lat);
          }}
        />
        <ActivityFeed
          pins={data}
          posts={postsData}
          enabled={feedEnabled}
          paused={panelOpen && users.length > 0}
          lang={lang}
        />
      </div>
      <LoginControl auth={auth} lang={lang} />
      <UserMenu
        auth={auth}
        profileOverride={profileOverride}
        lang={lang}
        trackingActive={tracking.active}
      >
        <CheckinControl auth={auth} lang={lang} />
        <TrackingControl auth={auth} tracking={tracking} lang={lang} />
        <ProfileControl
          auth={auth}
          currentProfile={profileOverride}
          onProfileChange={setProfileOverride}
          lang={lang}
        />
        <StatusControl auth={auth} lang={lang} />
        <PostControl auth={auth} onPosted={refreshPosts} lang={lang} />
      </UserMenu>

      {showOverlay && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            background: '#eef1f4',
            color: '#4a5568',
            font: '14px/1.5 system-ui, sans-serif',
          }}
        >
          <div className="app-spinner" />
          <div>{m.app.preparing}</div>
        </div>
      )}
    </div>
  );
}
